import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Image, Users, Truck, ClipboardList, LogOut, ChevronRight, AlertCircle
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import './Sidebar.css';

const navSections = [
    {
      label: 'Overview',
      items: [
        { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { to: '/artworks', icon: Image, label: 'Artworks' },
      ],
    },
    {
      label: 'Management',
      items: [
        { to: '/users', icon: Users, label: 'Team Members', adminOnly: true },
        { to: '/suppliers', icon: Truck, label: 'Suppliers', adminOnly: true },
        { to: '/audit-log', icon: ClipboardList, label: 'Audit Log', adminOnly: true },
      ],
    },
  ];

export default function Sidebar() {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (showLogoutConfirm) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showLogoutConfirm]);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(false);
    logout();
    navigate('/admin/login');
  };

  const handleNavClick = (path) => {
    if (location.pathname === path) {
      // Dispatch custom event to refresh data on current page
      window.dispatchEvent(new CustomEvent('navigationRefresh'));
    }
  };

  const roleLabels = {
    superadmin: 'Super Admin',
    admin: 'Administrator',
    rd: 'R&D Department',
    regulatory: 'Regulatory',
    qa: 'Quality Assurance',
    coo: 'ED Operations',
    marketing: 'Marketing'
  };

  return (
    <aside className="sidebar">
      {/* Logo */}
      <div className="sidebar-logo">
        <img src="/chivita.png" alt="Chivita logo" className="sidebar-logo-img" />
        <div className="sidebar-logo-text">
          <span className="sidebar-logo-name">Laminate</span>
          <span className="sidebar-logo-sub">Artflow</span>
        </div>
      </div>

      {/* Nav */}
      <nav className="sidebar-nav">
        {navSections.map(section => (
          <div key={section.label} className="sidebar-section">
            <p className="sidebar-section-label">{section.label}</p>
            {section.items.map(item => {
              if (item.adminOnly && !isAdmin) return null;

              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => handleNavClick(item.to)}
                  className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
                >
                  <Icon size={18} />
                  <span>{item.label}</span>
                  <ChevronRight size={14} className="sidebar-link-arrow" />
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User info */}
      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-user-info">
            <p className="sidebar-user-name">{user?.name}</p>
            <p className="sidebar-user-role">{roleLabels[user?.role] || user?.role}</p>
            {user?.email && <p className="sidebar-user-email">{user.email}</p>}
          </div>
        </div>
        <button className="sidebar-logout" onClick={handleLogoutClick} title="Sign out">
          <LogOut size={16} />
        </button>
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          pointerEvents: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px rgba(0, 0, 0, 0.15)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <AlertCircle size={20} color="#C8102E" />
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111' }}>
                Sign Out?
              </h3>
            </div>
            <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '0.9375rem' }}>
              Are you sure you want to sign out? You'll need to log in again to access your account.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  backgroundColor: '#f5f5f5',
                  color: '#333',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#e5e5e5'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: '#C8102E',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#a00826'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#C8102E'}
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
